import { useEffect, useState } from "react";
import { isStandalone, isIOS } from "@/lib/pwa";
import { Button } from "@/components/ui/button";
import { Download, Share, X, Smartphone } from "lucide-react";

const KEY = "autheuil_install_dismissed";
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("autheuil:installable"));
  });
}

export default function InstallBanner({ dark }) {
  const [installable, setInstallable] = useState(!!deferredPrompt);
  const [hidden, setHidden] = useState(() => isStandalone() || sessionStorage.getItem(KEY) === "1");
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const on = () => setInstallable(true);
    const done = () => { setInstalled(true); setTimeout(() => setHidden(true), 4000); };
    window.addEventListener("autheuil:installable", on);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("autheuil:installable", on); window.removeEventListener("appinstalled", done); };
  }, []);

  const ios = isIOS() && !installable;
  if (hidden || (!installable && !ios)) return null;

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (outcome !== "accepted") setInstallable(false);
  };
  const dismiss = () => { sessionStorage.setItem(KEY, "1"); setHidden(true); };

  const base = dark
    ? "bg-slate-800/70 border-slate-700 text-slate-100"
    : "bg-white border-slate-200 text-slate-900";

  return (
    <div data-testid="install-banner" className={`no-print rounded-2xl border-2 p-4 flex flex-wrap items-center gap-3 ${base}`}>
      <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
        <Smartphone className="w-6 h-6 text-amber-400" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="font-heading font-black uppercase tracking-wide text-sm">
          {installed ? "Application installée" : "Installer sur l'écran d'accueil"}
        </div>
        <div className={`text-xs font-medium ${dark ? "text-slate-300" : "text-slate-500"}`}>
          {installed
            ? "Retrouvez « Autheuil » parmi vos applications."
            : ios
              ? <>Sur iPad / iPhone : touchez <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> <b>Partager</b> puis <b>« Sur l'écran d'accueil »</b>.</>
              : "Lancement en un geste au quai, plein écran, sans barre d'adresse."}
        </div>
      </div>
      {!installed && installable && (
        <Button data-testid="install-app-btn" onClick={install} className="h-11 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold">
          <Download className="w-5 h-5 mr-1" /> Installer
        </Button>
      )}
      <button data-testid="install-dismiss-btn" onClick={dismiss} aria-label="Fermer"
        className={`p-2 rounded-lg ${dark ? "hover:bg-white/10" : "hover:bg-slate-100"}`}>
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
